import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Chip } from '@mui/material';
import { useAppNavigation } from '../hooks/useAppNavigation';
import api from '../services/api';

const TagCloud = ({ maxTags = 50 }) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useAppNavigation();

  useEffect(() => {
    const fetchTagCloud = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/tags/cloud?max=${maxTags}`);
        setTags(response.data.tags || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching tag cloud:', err);
        setError('Failed to load tags. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTagCloud();
  }, [maxTags]);

  const handleTagClick = (tag) => {
    // Navigate to Find page with tag-specific search param for exact tag matching
    navigate(`find?tagSearch=${encodeURIComponent(tag)}`);
  };

  // Color palette for tags
  const getTagColor = (weight) => {
    const colors = [
      '#E0F7FA', // weight 1 (lightest)
      '#B2EBF2',
      '#80DEEA',
      '#4DD0E1',
      '#26C6DA',
      '#00BCD4',
      '#00ACC1',
      '#0097A7',
      '#00838F',
      '#006064'  // weight 10 (darkest)
    ];
    return colors[weight - 1] || colors[5]; // Default to middle color if weight is invalid
  };

  // Font size based on weight (1-10)
  const getTagSize = (weight) => {
    const baseSize = 0.75; // base size in rem
    const increment = 0.075; // size increment per weight level
    return (baseSize + (weight - 1) * increment).toFixed(2) + 'rem';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (tags.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No tags found. Add tags to your items to see them here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1,
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        {tags.map((tag) => (
          <Chip
            key={tag.tag}
            label={tag.tag}
            onClick={() => handleTagClick(tag.tag)}
            sx={{
              backgroundColor: getTagColor(tag.weight),
              fontSize: getTagSize(tag.weight),
              '&:hover': {
                backgroundColor: '#0097A7',
                color: 'white',
              },
              transition: 'all 0.2s ease',
              m: 0.5,
              p: 0.5
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default TagCloud;
